import {
	createFileRoute,
	redirect,
	useLoaderData,
	useNavigate,
	useRouteContext,
} from "@tanstack/react-router";
import type { postsUpdateQuery } from "./__generated__/postsUpdateQuery.graphql";
import {
	ConnectionHandler,
	fetchQuery,
	graphql,
	useMutation,
} from "react-relay";
import { environment } from "@/relay/environment";
import type { postsUpdateMutation } from "./__generated__/postsUpdateMutation.graphql";
import { PostForm, type PostFormSchemaValues } from "@/forms/post-form";
import { useEditor } from "@/editor/editor";
import { Card } from "@/components/ui/card";
import { Heading } from "@/components/heading";
import { EditorContent } from "@tiptap/react";
import { AnalyticsEvents, useAnalytics } from "@/utils/analytics";
import { toast } from "sonner";
import { isAfter } from "date-fns";

export const Route = createFileRoute("/_visibility/_topnav/posts/$slug/editar")(
	{
		component: RouteComponent,
		loader: async ({ context: { club }, params }) => {
			const data = await fetchQuery<postsUpdateQuery>(
				environment,
				graphql`
  	    query postsUpdateQuery($club: String!, $slug: String!) {
          post(club: $club, slug: $slug) {
             id

             ...on Post {
               title
               slug
               content
               space {
                 id
               }
               commentPolicy
               scheduledPublishDate
               pinned
               pinnedUniversal
               hideMetadata
               coverContent {
                  ... on File {
                     id
                     name
                     size
                     signedUrl
                  }
                  ... on VideoFile {
                      muxId
                  }
               }
               attachments {
                 id
                 name
                 size
                 signedUrl
               }
             }
          }
  				club(slug: $club) {
  				  groups {
  						spaces {
  						  id
  							name
  							slug
  							icon
  							isMember
  							visibility
  						}
  				  }
  					rules {
  						title
  						description
  					}
  				}
  			}
  		`,
				{ club: club.slug, slug: params.slug },
			).toPromise();

			const groups = data?.club?.groups || [];
			const allSpaces = groups.filter(Boolean).flatMap((g) => g.spaces);
			const availableSpaces =
				club.permissions.length > 0
					? allSpaces.filter(Boolean)
					: allSpaces.filter(
							(s) => s && (s.visibility === "PUBLIC" || s.isMember),
						);

			if (!data?.post) {
				throw redirect({
					to: "/comunidade",
				});
			}

			return { availableSpaces, rules: data.club.rules, post: data.post };
		},
	},
);

function RouteComponent() {
	const navigate = useNavigate({ from: Route.fullPath });

	const { club, session } = useRouteContext({ from: "__root__" });

	const { post, rules, availableSpaces } = useLoaderData({
		from: Route.id,
	});

	const [mutate] = useMutation<postsUpdateMutation>(graphql`
	   mutation postsUpdateMutation($payload: UpdatePostInput!, $slug: String!, $club: String!) {
				updatePost(input: $payload, postSlug: $slug, club: $club) {
	        id
					slug
					space {
					  id
					  slug
					}
					club {
					  id
					}

					...postPreviewFragment @arguments(club: $club)
					...postFragment @arguments(club: $club)
				}
			}
	`);

	const { track } = useAnalytics();

	const isModerator =
		club.permissions?.some((p) => p.slug === "MODERATOR") || false;
	const isAdmin =
		club.permissions?.some((p) => p.slug === "CLUB_ADMIN") || false;

	const onSubmit = (value: PostFormSchemaValues) => {
		return mutate({
			variables: {
				slug: post.slug!,
				club: club.slug,
				payload: {
					title: value.title,
					content: JSON.stringify(value.content),
					space: value.space,
					club: club.slug,
					cover: value.cover
						? { coverContent: value.cover.id }
						: { coverContent: null },
					attachments: value.attachments.map((it) => ({ file: it.id })),

					...(isAdmin
						? {
								notifyMembers: value.notifyUsers,
							}
						: {}),
					...(isModerator || isAdmin
						? {
								scheduledPublishDate: value.schedule
									? value.scheduledDate
									: null,
								commentPolicy: value.commentPolicy,
								pinned: value.pinned,
								pinnedUniversal: value.pinnedUniversal,
								hideMetadata: value.hideMetadata,
							}
						: {}),
				},
			},
			onCompleted: (res) => {
				track(AnalyticsEvents.POST_EDITED, { post_id: post.id });

				toast.success("Post editado com sucesso!");

				navigate({
					to: "/comunidade/s/$space/posts/$post",
					params: {
						space: res.updatePost.space?.slug!,
						post: res.updatePost.slug,
					},
				});
			},
			onError: () => {
				toast.error(
					"Ocorreu um erro ao criar o post. Tente novamente mais tarde.",
				);
			},
			updater: (store, res) => {
				if (res?.updatePost.space?.id) {
					const item = store.get(res?.updatePost.space.id);

					if (item) {
						const bestConnection = ConnectionHandler.getConnection(
							item,
							"spaceFeedFragment__feed",
							{ ignoreFixed: false, orderBy: "BEST" },
						);

						const newestConnection = ConnectionHandler.getConnection(
							item,
							"spaceFeedFragment__feed",
							{ ignoreFixed: false, orderBy: "NEWEST" },
						);

						const oldestConnection = ConnectionHandler.getConnection(
							item,
							"spaceFeedFragment__feed",
							{ ignoreFixed: false, orderBy: "OLDEST" },
						);

						bestConnection?.invalidateRecord();
						newestConnection?.invalidateRecord();
						oldestConnection?.invalidateRecord();
					}
				}

				if (res?.updatePost.club?.id) {
					const clubItem = store.get(res?.updatePost.club.id);

					if (clubItem) {
						const bestClubConnection = ConnectionHandler.getConnection(
							clubItem,
							"comunidadeFeedFragment_feed",
							{ ignoreFixed: false, orderBy: "BEST" },
						);

						const newestClubConnection = ConnectionHandler.getConnection(
							clubItem,
							"comunidadeFeedFragment_feed",
							{ ignoreFixed: false, orderBy: "NEWEST" },
						);

						const oldestClubConnection = ConnectionHandler.getConnection(
							clubItem,
							"comunidadeFeedFragment_feed",
							{ ignoreFixed: false, orderBy: "OLDEST" },
						);

						bestClubConnection?.invalidateRecord();
						newestClubConnection?.invalidateRecord();
						oldestClubConnection?.invalidateRecord();
					}
				}
			},
		});
	};

	const rulesEditor = useEditor({
		editable: false,
		content: rules?.description,
		session,
		club: club.slug,
		mimeTypes: ["*"],
		size: "sm",
		features: {
			colors: { enabled: true },
			button: { enabled: true },
			mention: { enabled: true },
			files: { enabled: false },
		},
	});

	return (
		<div className="w-full flex flex-row items-start justify-center gap-6 p-6">
			<Card className="p-6 w-full max-md:px-4 max-w-[700px]">
				<div className="flex flex-col gap-6">
					<Heading>Editar Post</Heading>

					<PostForm
						onSubmit={onSubmit}
						spaces={availableSpaces}
						defaultValues={{
							title: post.title!,
							content: post.content,
							space: post.space?.id!,
							cover: post.coverContent?.id
								? {
										id: post.coverContent.id!,
										url: post.coverContent?.signedUrl!,
										muxId: post.coverContent?.muxId || undefined,
									}
								: null,
							attachments:
								post.attachments?.map((att) => ({
									...att,
									url: att.signedUrl,
								})) || [],
							schedule: !!(
								post.scheduledPublishDate &&
								isAfter(new Date(post.scheduledPublishDate), new Date())
							),
							scheduledDate:
								post.scheduledPublishDate &&
								isAfter(new Date(post.scheduledPublishDate), new Date())
									? post.scheduledPublishDate
									: null,
							commentPolicy:
								post.commentPolicy === "%future added value"
									? "ALLOWED"
									: post.commentPolicy || "ALLOWED",
							pinned: post.pinned || false,
							pinnedUniversal: post.pinnedUniversal || false,
							hideMetadata: post.hideMetadata || false,
							notifyUsers: false,
						}}
					/>
				</div>
			</Card>

			{rules && (
				<Card className="hidden h-[70dvh] md:flex p-6 flex-col gap-6 overflow-y-auto w-[340px] min-w-[340px] max-w-[340px]">
					<Heading level={3}>Regras da Comunidade</Heading>

					<EditorContent editor={rulesEditor} />
				</Card>
			)}
		</div>
	);
}
